/**
 * RunTestcases component is responsible for executing and displaying the status of 
 * both visible and hidden test cases for a student's programming submission. It 
 * interacts with the backend to fetch the execution status of test cases, triggers 
 * their execution, and displays the results.
 *
 * Key Features:
 * - Fetches and displays the execution status of visible and hidden test cases.
 * - Provides buttons to run visible and hidden test cases.
 * - Displays a loading spinner during the test case execution.
 * - Shows the count of passed test cases out of total test cases.
. The buttons to run test cases are conditionally rendered 
 * based on the execution status and access control variables.
 */
import log from "loglevel";

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import httpService from 'services/httpService.js';
import * as constants from "Constants";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import TestCaseModal from 'Navigation/Course/Content/Programming/Instructor/AllSubmissions/TestCaseModal.jsx'

function RunTestcases(props) {
  log.log("props",props)
  const userId = useSelector((state) => state.userInfo.userId);
  // state for execution status of visible test cases
  const [visibleTestcaseExecutionPending, setVisibleTestcaseExecutionPending] =
    useState(true);
  //state for execution status of hidden testcases
  const [hiddenTestcaseExecutionPending, setHiddenTestcaseExecutionPending] =
    useState(true);
  const [toolTipMessageVisible,setToolTipMessageVisible] = useState("Click to run testcases")
  const [toolTipMessageHidden, setToolTipMessageHidden] = useState("Click to run testcases")
  const [visibleTestCaseModal, setVisibleTestCaseModal] = useState(false)
  const [hiddenTestCaseModal, setHiddenTestCaseModal] = useState(false)
  const [studentEmail, setStudentEmail] = useState("")
  const [studentName, setStudentName] = useState("")

  const fetchUserProfile = async (userId) => {
    const response = await httpService.get(`/accounts/api/profile/`);
    const profile = response.data?.[0];
    log.log("response, profile", profile)
    setStudentEmail(profile?.email);
    setStudentName(`${profile?.first_name} ${profile?.last_name}`);
  };

  useEffect(()=>{
    const fetchData = async() =>{
      try{
        await fetchUserProfile(userId);
      }catch(error){
        log.log(error)
      }
    }
    fetchData();
  },[])

  // Update execution pending status from selectedSubmission prop
  // This avoids duplicate API call since ManageSubmissions already fetches this data
  useEffect(() => {
    if (props.selectedSubmission) {
      if (props.selectedSubmission.execution_pending_for_hidden_testcases === false) {
        setHiddenTestcaseExecutionPending(false);
      }
      if (props.selectedSubmission.execution_pending_for_visible_testcases === false) {
        setVisibleTestcaseExecutionPending(false);
      }
    }
  }, [props.selectedSubmission]);

  useEffect(() => {
    if (!props.access?.enable_show_visible_marks) {
      setToolTipMessageVisible("Click to run testcases");
    }else if(!props.access?.enable_show_hidden_marks){
      setToolTipMessageHidden("Click to run testcases");
    }
  }, [
    props.access?.enable_show_visible_marks,
    props.access?.enable_show_hidden_marks,
  ]);

  const runTestCaseButton = async (value) => {
    // run hidden and visible test case function
    props.setIsLoading(true); //loading spinner component called
    const url = constants.base_url + "/evalpro/evaluate/" + value + "/" + props.selectedSubmission?.id;
    let form_data = new FormData();
    form_data.append('student_id', props.selectedSubmission?.student);

    try {
        const response = await httpService.put(url, form_data, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        //setting test results
        props.setTestResults(response.data.testcase_details);
        //setting access control variables
        props.changeAccess(response.data.access_control_vars);
        props.setIsLoading(false); //loading spinner component called off
        props.setIsSubmissionChanged(false);
        if(value === "hidden"){
          setToolTipMessageHidden(
            response.data.passed_testcases_count +
            "/" +
            response.data.testcase_details.length +
            " " +
          " testcases passed"
          )
        } else if(value === "visible"){
          setToolTipMessageVisible(
            response.data.passed_testcases_count +
            "/" +
            response.data.testcase_details.length +
            " " +
          " testcases passed"
          )
        }
    } catch (error) {
        props.setIsLoading(false); //loading spinner component called off
        log.log(error);
    }
  };
  
  return (             
    <>
      <div className="flex justify-space-between justify-content-left">
    
            {visibleTestcaseExecutionPending === true ? null : (
              <>
                {props.access?.enable_display_visible_testcases &&
                props.access?.enable_show_visible_marks ? (
                  <>
                  <OverlayTrigger
                    id="run_visible_testcases"
                    overlay={
                      <Tooltip>{toolTipMessageVisible || " "}</Tooltip>
                    }
                  >
                    <Button
                      ref = {props.practiceMarks}
                      id="practice_marks"
                      onClick={(e) => runTestCaseButton("visible")}
                      variant="primary"
                      disabled={props.isLoading} //button disabled while loading true
                      style={{marginRight:"30px", marginBottom:"20px"}}
                    >
                      Run Visible Testcases
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    id="visible_testcases_diff"
                    overlay={
                      <Tooltip>{"see diff of visible testcases"}</Tooltip>
                    }
                  >
                    <Button
                      id="visible_testcase_diff"
                      variant="primary"
                      disabled={props.isLoading} //button disabled while loading true
                      style={{marginRight:"30px", marginBottom:"20px"}}
                      onClick={()=>{setVisibleTestCaseModal(true)}}
                    >
                      Visible Testcases Compare Output
                    </Button>
                  </OverlayTrigger>
                  </>
                ) : null}
              </>
            )}
          
            {hiddenTestcaseExecutionPending === true ? null : (
              <>
                {props.access?.enable_display_hidden_testcases &&
                props.access?.enable_show_hidden_marks ? (
                  <>
                    <OverlayTrigger
                      id="run_hidden_testcases"
                      overlay={
                        <Tooltip>{toolTipMessageHidden || " "}</Tooltip>
                      }
                    >
                      <Button
                        ref = {props.evaluateMarks}
                        id="evaluate_marks"
                        onClick={(e) => runTestCaseButton("hidden")}
                        variant="primary"
                        disabled={props.isLoading} //button disabled while loading true
                        style={{marginBottom:"20px", marginRight:"30px"}}
                      >
                        Run Hidden Testcases
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      id="hidden_testcases_diff"
                      overlay={
                        <Tooltip>{"see diff of hidden testcases"}</Tooltip>
                      }
                    >
                      <Button
                        id="visible_testcase_diff"
                        variant="primary"
                        disabled={props.isLoading} //button disabled while loading true
                        style={{marginRight:"30px", marginBottom:"20px"}}
                        onClick={()=>{setHiddenTestCaseModal(true)}}
                      >
                        Hidden Testcases Compare Output
                      </Button>
                    </OverlayTrigger>
                  </>
                ) : null}
              </>
            )}
      
      {visibleTestCaseModal &&
        <TestCaseModal
          showModal={visibleTestCaseModal}
          setShowModal={setVisibleTestCaseModal}
          studentName={studentName || "student"}
          studentEmail={studentEmail || "student@example.com"}
          obtainedMarks={props.selectedSubmission?.obtained_visible_marks || '0'}
          submissionID={props.selectedSubmission?.id || '-1'}
          testcaseType={"visible_testcases"}
        />
      }
      {hiddenTestCaseModal &&
        <TestCaseModal
          showModal={hiddenTestCaseModal}
          setShowModal={setHiddenTestCaseModal}
          studentName={studentName || "student"}
          studentEmail={studentEmail || "student@example.com"}
          obtainedMarks={props.selectedSubmission?.obtained_hidden_marks || '0'}
          submissionID={props.selectedSubmission?.id || '-1'}
          testcaseType={"hidden_testcases"}
        />
      } 
    </div> 
  </>  
  );
}
export default RunTestcases;
